import { useState } from "react";

import { IconButton } from "@/components/ui/icon-button";
import { MuiEditIcon } from "@/components/ui/mui-spec-icons";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import { useEditSpecificationSummary } from "@/hooks/spec-editor-summary";

import {
  EditSpecHeaderDialog,
  type SpecHeaderEditFields,
  type SpecHeaderEditInput,
} from "./edit-spec-name-dialog";

// The Editor header's pencil — the donor's `EditSpecSummary`
// (`SpecEditIsland/components/Header/EditSpecSummary/EditSpecSummary.tsx`)
// (#806): a large primary icon button, `Edit Specification`, that opens the
// spec's dialog for its type and writes through #799's header hook.
//
// **When the dialog closes** is each donor's:
//
//   - core / base (`SpecificationFormDialog`): once the write settles, either
//     way. The donor awaits its RTK trigger without `unwrap`, which never
//     throws, so a failure closes the dialog too and the header keeps the
//     stored values the server still holds (#806 Assumption 3);
//   - community (the island's `edit-with-notes`): on success only. A failure
//     keeps it open under `Error updating Community Specification. Please try
//     again.`, and the next opening starts clean.
//
// The write's result lands through the Editor's one updater, so the heading,
// the notes block, the breadcrumb and the band read the saved values in the
// frame the dialog closes in (ADR-0014 decision 1).

export function EditSpecSummary({
  spec,
  target,
}: {
  spec: SpecHeaderEditFields;
  target: SpecEditorTarget;
}) {
  const [open, setOpen] = useState(false);
  const edit = useEditSpecificationSummary();
  const isCommunity = spec.type === "community";

  const submit = (input: SpecHeaderEditInput) =>
    edit.mutate(
      { ...target(), ...input },
      isCommunity ? { onSuccess: () => setOpen(false) } : { onSettled: () => setOpen(false) },
    );

  return (
    <>
      <IconButton
        size="large"
        color="primary"
        aria-label="Edit Specification"
        data-testid="spec-edit-btn"
        onClick={() => {
          // A failure's snackbar belongs to the opening it happened in.
          edit.reset();
          setOpen(true);
        }}
      >
        <MuiEditIcon />
      </IconButton>
      <EditSpecHeaderDialog
        open={open}
        onOpenChange={setOpen}
        spec={spec}
        pending={edit.isPending}
        failed={isCommunity && edit.isError}
        onErrorClose={edit.reset}
        onSubmit={submit}
      />
    </>
  );
}
